"use client";

import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useCrossSurfaceConfirmationStore } from "@/lib/stores/crossSurfaceConfirmation";

interface Props {
  onNavigate: (tab: string) => void;
}

export default function CrossSurfaceConfirmationBanner({ onNavigate }: Props) {
  const confirmation = useCrossSurfaceConfirmationStore((state) => state.confirmation);
  const clearConfirmation = useCrossSurfaceConfirmationStore((state) => state.clearConfirmation);
  const triggerReveal = useCrossSurfaceConfirmationStore((state) => state.triggerReveal);

  useEffect(() => {
    if (!confirmation) return;
    const timeoutId = window.setTimeout(() => {
      clearConfirmation();
    }, 6500);
    return () => window.clearTimeout(timeoutId);
  }, [clearConfirmation, confirmation]);

  if (!confirmation) {
    return null;
  }

  const hasCta = Boolean(confirmation.ctaTab || confirmation.revealTarget);
  const badge =
    confirmation.state === "committed"
      ? { label: "Committed", className: "bg-forest-med text-white" }
      : confirmation.state === "retained"
        ? { label: "Kept", className: "bg-amber/15 text-amber" }
        : confirmation.state === "suggested"
          ? { label: "Suggested", className: "bg-blue/10 text-blue" }
          : { label: "Update", className: "bg-card text-text-light" };

  return (
    <AnimatePresence>
      <motion.div
        key={confirmation.id}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="mx-4 mb-4 mt-4 flex items-center justify-between gap-3 rounded-2xl border border-forest/15 bg-sage-pale/45 px-4 py-3 lg:mx-0 lg:mt-6"
        data-testid="cross-surface-confirmation"
      >
        <div className="min-w-0">
          <span
            className={`inline-flex rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${badge.className}`}
            data-testid="cross-surface-confirmation-state"
          >
            {badge.label}
          </span>
          <p className="mt-2 text-sm font-medium leading-relaxed text-forest">{confirmation.message}</p>
          {confirmation.truth ? (
            // E2E routing trace. Previously stored as textContent in a
            // sr-only + aria-hidden <p>, but dogfood R3 (2026-05-27)
            // showed that snapshot tools and automation can still read
            // textContent regardless of sr-only/aria-hidden. The 2026-04-21
            // "U2 — truth banner leaks raw IDs" report was previously
            // dismissed as a misframing; round-3 dogfood proved the leak
            // is real for any tool that walks the DOM tree.
            //
            // Fix: move the routing trace to data-* attributes. Playwright
            // can still assert via getByTestId(...).getAttribute(...) and
            // tests are updated accordingly. The element itself has zero
            // textContent so it can never leak through textContent / OCR /
            // screenshot snapshots.
            <span
              data-testid="cross-surface-confirmation-truth"
              data-target-kind={confirmation.truth.targetKind}
              data-target-id={confirmation.truth.targetId}
              data-destination-layer={confirmation.truth.destinationLayer}
              data-owning-service={confirmation.truth.owningService}
              data-proposal-id={confirmation.truth.proposalId ?? ""}
              data-authority-level={confirmation.truth.authorityLevel ?? ""}
              data-requires-confirmation={
                confirmation.truth.requiresConfirmation == null
                  ? ""
                  : confirmation.truth.requiresConfirmation
                    ? "requires-confirmation"
                    : "no-confirmation"
              }
              hidden
            />
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {hasCta ? (
            <button
              type="button"
              onClick={() => {
                if (confirmation.revealTarget) {
                  triggerReveal();
                }
                if (confirmation.ctaTab) {
                  onNavigate(confirmation.ctaTab);
                }
              }}
              className="shrink-0 rounded-full border border-forest/15 bg-white px-3 py-1.5 text-xs font-semibold text-forest transition-colors hover:border-forest/30"
            >
              {confirmation.ctaLabel ?? "Reveal item"}
            </button>
          ) : null}
          <button
            type="button"
            aria-label="Dismiss confirmation"
            onClick={clearConfirmation}
            className="rounded-full px-2 py-1 text-xs font-semibold text-muted transition-colors hover:bg-white/70 hover:text-text"
          >
            Hide
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
